import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import dayjs from 'dayjs'
import 'dayjs/locale/uk' // Ukrainian
import 'dayjs/locale/ru' // Russian
import 'dayjs/locale/fr' // French
import 'dayjs/locale/de' // German
import 'dayjs/locale/it' // Italian
import 'dayjs/locale/es' // Spanish
import 'dayjs/locale/pl' // Polish
import 'dayjs/locale/nb' // Norwegian Bokmål
import 'dayjs/locale/nn' // Norwegian Nynorsk
import 'dayjs/locale/sv' // Swedish
import 'dayjs/locale/fi' // Finnish
import 'dayjs/locale/ms' // Malay
import 'dayjs/locale/sq' // Albanian
import 'dayjs/locale/pt-br' // Brazilian Portuguese
import 'dayjs/locale/se' // Northern Sami
import 'dayjs/locale/is' // Icelandic
import 'dayjs/locale/lt' // Lithuanian
import 'dayjs/locale/nl' // Dutch
import 'dayjs/locale/ja' // Japanese
import 'dayjs/locale/ro' // Romanian
import 'dayjs/locale/da' // Danish
import 'dayjs/locale/hu' // Hungarian
import 'dayjs/locale/et' // Estonian
import 'dayjs/locale/lv' // Latvian
import 'dayjs/locale/zh' // Chinese
import 'dayjs/locale/el' // Greek
import 'dayjs/locale/pt' // Portuguese

interface FormatPeriodOptions {
    dateFormat?: string
    presentText?: string
    invalidDateText?: string
    rangeSeparator?: string
    lang?: string
}

interface FormatDurationOptions {
    showMonths?: boolean
    invalidDateText?: string
    lang?: string
}

export function useFormattedDatesPeriod() {
    const { getTitle } = useApplyV2NavTranslations()

    const pluralRules: Record<string, Record<string, string[]>> = {
        uk: {
            year: ['рік', 'роки', 'років'],
            month: ['місяць', 'місяці', 'місяців'],
        },
        ru: {
            year: ['год', 'года', 'лет'],
            month: ['месяц', 'месяца', 'месяцев'],
        },
    }

    const getPluralForm = (count: number, unit: string, lang: string): string => {
        if (!pluralRules[lang]?.[unit]) {
            // return count !== 1 ? `${unit}s` : unit
            return unit
        }

        const forms = pluralRules[lang][unit]
        const lastTwo = count % 100
        const lastOne = count % 10

        if (lang === 'uk' || lang === 'ru') {
            if (lastTwo >= 11 && lastTwo <= 19) return forms[2]
            if (lastOne === 1) return forms[0]
            if (lastOne >= 2 && lastOne <= 4) return forms[1]
            return forms[2]
        }

        // Default to singular/plural for other languages
        return count !== 1 ? forms[1] : forms[0]
    }

    return {
        formatDateRange: (fromDate?: string | null, toDate?: string | null, options?: FormatPeriodOptions): string => {
            const {
                dateFormat = 'MMM YYYY',
                presentText = getTitle('1226', 'Present'),
                invalidDateText = getTitle('148', 'Invalid date'),
                rangeSeparator = ' – ',
                lang = document?.documentElement?.lang ?? 'en',
            } = options || {}

            dayjs.locale(lang)

            if (!fromDate && !toDate) return ''

            try {
                const from = fromDate ? dayjs(fromDate) : null
                const to = toDate ? dayjs(toDate) : dayjs()

                const fromFormatted = from?.format(dateFormat) ?? ''
                const toFormatted = toDate ? to.format(dateFormat) : presentText

                return [fromFormatted, toFormatted].filter(Boolean).join(rangeSeparator)
            } catch (error) {
                console.error('Error formatting date range:', error)
                return options?.invalidDateText || getTitle('148', 'Invalid date')
            }
        },

        formatDuration: (fromDate?: string | null, toDate?: string | null, options?: FormatDurationOptions): string => {
            const {
                showMonths = true,
                invalidDateText = getTitle('148', 'Invalid date'),
                lang = document?.documentElement?.lang ?? 'en',
            } = options || {}

            dayjs.locale(lang)

            if (!fromDate) return ''

            try {
                const from = dayjs(fromDate)
                const to = toDate ? dayjs(toDate) : dayjs()

                const years = to.diff(from, 'year')
                const months = to.diff(from.add(years, 'year'), 'month')

                const durationParts = []
                if (years > 0) {
                    durationParts.push(`${years} ${years !== 1 ? getTitle('3120', 'Years') : getTitle('1322', 'Year')}`)
                }
                if (showMonths && months > 0) {
                    durationParts.push(
                        `${months} ${months !== 1 ? getTitle('2763', 'Months') : getTitle('931', 'Month')}`
                    )
                }

                return durationParts.join(' ').toLowerCase()
            } catch (error) {
                console.error('Error formatting duration:', error)
                return invalidDateText
            }
        },
    }
}

export default useFormattedDatesPeriod
